const settingsKind = "Settings";
const settingsKindValue = 7;
const customerKinds = new Set(["Individual", "Business"]);
const controlCharacters = /[\u0000-\u001f\u007f]/u;
const sqlRowVersion = /^[A-Za-z0-9+/]{11}=$/u;

function requiredIdentity(value, label) {
  const normalized = String(value ?? "").trim();
  if (!normalized || normalized.length > 64 || controlCharacters.test(normalized))
    throw new Error(`${label} is unavailable. Refresh your signed-in account before trying again.`);
  return normalized;
}

function optionalText(value, maximum, label) {
  if (value == null || String(value).trim() === "") return null;
  const normalized = String(value).trim();
  if (normalized.length > maximum || controlCharacters.test(normalized))
    throw new Error(`${label} must use no more than ${maximum} printable characters.`);
  return normalized;
}

function requiredText(value, minimum, maximum, label) {
  const normalized = optionalText(value, maximum, label);
  if (!normalized || normalized.length < minimum)
    throw new Error(`${label} must use ${minimum}-${maximum} printable characters.`);
  return normalized;
}

function snapshotBoundary(snapshot, expectedOrganizationId) {
  const organizationId = requiredIdentity(expectedOrganizationId, "The Cloud organization");
  if (!snapshot || String(snapshot.organizationId ?? "").trim() !== organizationId ||
      typeof snapshot.workspaceGeneration !== "string" || !snapshot.workspaceGeneration.trim() ||
      !Number.isSafeInteger(snapshot.workspaceVersion) || snapshot.workspaceVersion < 1 ||
      !Array.isArray(snapshot.entities))
    throw new Error("Pirdy returned an invalid Cloud preferences snapshot.");
  return organizationId;
}

export function normalizeWorkspaceSettings(input) {
  const displayName = requiredText(input?.displayName, 2, 80, "Display name");
  const businessName = optionalText(input?.businessName, 80, "Business name");
  const customerKind = String(input?.customerKind ?? "").trim();
  if (!customerKinds.has(customerKind))
    throw new Error("Choose Individual or Business for the customer type.");
  const countryCode = String(input?.countryCode ?? "").trim().toUpperCase();
  if (!/^[A-Z]{2}$/u.test(countryCode))
    throw new Error("Country code must contain two letters, for example IN.");
  const preferredLanguage = requiredText(input?.preferredLanguage, 2, 40, "Preferred language");
  return { displayName, businessName, customerKind, countryCode, preferredLanguage };
}

export function readWorkspaceSettingsSnapshot(snapshot, expectedOrganizationId, fallback = {}) {
  const organizationId = snapshotBoundary(snapshot, expectedOrganizationId);
  const matches = snapshot.entities.filter(entity => entity?.kind === settingsKind || entity?.kind === settingsKindValue);
  if (matches.length > 1) throw new Error("Pirdy returned duplicate Cloud preferences.");
  const entity = matches[0] ?? null;
  if (entity) {
    const deletion = entity.deletion ?? 0;
    if (String(entity.id ?? "") !== organizationId || (deletion !== 0 && deletion !== "None") ||
        !Number.isSafeInteger(entity.version) || entity.version < 1 || !entity.payload ||
        String(entity.payload.organizationId ?? "") !== organizationId ||
        (entity.cloudRowVersion != null && !sqlRowVersion.test(entity.cloudRowVersion)))
      throw new Error("Pirdy returned invalid Cloud preferences metadata.");
  }

  const payload = entity?.payload ?? {};
  const loaded = {
    displayName: optionalText(payload.displayName, 80, "Display name"),
    businessName: optionalText(payload.businessName, 80, "Business name"),
    customerKind: payload.customerKind == null ? null : String(payload.customerKind).trim(),
    countryCode: payload.countryCode == null ? null : String(payload.countryCode).trim().toUpperCase(),
    preferredLanguage: optionalText(payload.preferredLanguage, 40, "Preferred language"),
  };
  if (loaded.customerKind && !customerKinds.has(loaded.customerKind))
    throw new Error("Pirdy returned an unsupported customer type.");
  if (loaded.countryCode && !/^[A-Z]{2}$/u.test(loaded.countryCode))
    throw new Error("Pirdy returned an invalid country preference.");

  return {
    organizationId,
    snapshot,
    entity,
    preferences: {
      displayName: loaded.displayName ?? String(fallback.displayName ?? "").trim(),
      businessName: loaded.businessName ?? String(fallback.businessName ?? "").trim(),
      customerKind: loaded.customerKind ?? (customerKinds.has(fallback.customerKind) ? fallback.customerKind : "Individual"),
      countryCode: loaded.countryCode ?? String(fallback.countryCode ?? "").trim().toUpperCase(),
      preferredLanguage: loaded.preferredLanguage ?? String(fallback.preferredLanguage ?? "").trim(),
    },
  };
}

export function createWorkspaceSettingsUpload(snapshotResult, accountId, preferences, options = {}) {
  const snapshot = snapshotResult?.snapshot;
  const organizationId = snapshotBoundary(snapshot, snapshotResult?.organizationId);
  const portableAccountId = requiredIdentity(accountId, "The portable Pirdy account");
  const normalized = normalizeWorkspaceSettings(preferences);
  const replicaId = `web-settings:${portableAccountId}`;
  const idempotencyKey = String(options.idempotencyKey ?? globalThis.crypto?.randomUUID?.() ?? "").trim();
  if (!idempotencyKey || idempotencyKey.length > 128 || /\s/u.test(idempotencyKey) || controlCharacters.test(idempotencyKey))
    throw new Error("This browser could not create a safe preferences request. Try again.");
  const now = options.now ?? new Date();
  if (!(now instanceof Date) || Number.isNaN(now.valueOf()))
    throw new Error("This browser could not timestamp the preferences request.");
  const entity = snapshotResult.entity;
  return {
    replicaId,
    workspaceGeneration: snapshot.workspaceGeneration,
    baselineVersion: snapshot.workspaceVersion,
    idempotencyKey,
    mutations: [{
      kind: settingsKind,
      id: organizationId,
      version: Math.max(0, Number(entity?.version ?? 0)) + 1,
      updatedAtUtc: now.toISOString(),
      replicaId,
      payload: { organizationId, ...normalized },
      deletion: 0,
      cloudRowVersion: entity?.cloudRowVersion ?? null,
    }],
  };
}

export const workspaceSettingsContract = Object.freeze({ settingsKind, settingsKindValue });
